/**
 * Hook que dispara um callback após N ms de inatividade (sem reset)
 * Usado para revelar o botão "Pular" após 60s sem interação (D5)
 */

import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook para detectar inatividade
 * 
 * @param {number} timeoutMs - Tempo de inatividade em ms (ex: 60000 para 60s)
 * @param {function} onTimeout - Callback chamado quando o timer expira
 * @returns {Object} { reset: function } - Resetar o timer
 * 
 * Uso:
 *   const timer = useInactivityTimer(60000, () => console.log('Inativo!'));
 *   
 *   // Resetar ao clicar
 *   <button onClick={() => timer.reset()}>Click</button>
 *   
 *   // Ao desmontar o componente, o timer é limpo automaticamente
 */
export const useInactivityTimer = (timeoutMs, onTimeout) => {
  const timeoutIdRef = useRef(null);
  const hasCalledRef = useRef(false);

  // Função para iniciar/reiniciar o timer
  const startTimer = useCallback(() => {
    // Limpa o timer anterior
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    // Reset da flag — permite chamar novamente se reiniciar
    hasCalledRef.current = false;

    // Inicia novo timer
    timeoutIdRef.current = setTimeout(() => {
      // Garante que onTimeout é chamado uma única vez
      if (!hasCalledRef.current && onTimeout) {
        hasCalledRef.current = true;
        onTimeout();
      }
    }, timeoutMs);
  }, [timeoutMs, onTimeout]);

  // Expõe função reset para ser chamada em interações
  const reset = useCallback(() => {
    startTimer();
  }, [startTimer]);

  // Inicia o timer ao montar
  useEffect(() => {
    startTimer();

    // Limpeza ao desmontar
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [startTimer]);

  return { reset };
};
